import { styled as muiStyled, useTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { useState } from 'react'
import Transition from 'react-transition-group/Transition'
import styled from 'styled-components'

import ClickAwayListener from '../../shared/ClickAwayListener'
import Icon from '../Icon'

const Wrapper = styled.div`
  position: relative;
`

const DropdownTrigger = muiStyled('button')(
  ({ theme, dropdownWidth, ...props }) => ({
    'position': 'relative',
    'display': 'block',
    'width': dropdownWidth === 'full' ? '100%' : '',
    'boxSizing': 'border-box',
    'cursor': 'pointer',
    'textAlign': 'left',
    'background': theme.palette.common.white,
    'border': (() => {
      switch (props.variant) {
        case 'rounded':
          return '1px solid rgba(0, 0, 0, 0.1)'
        case 'noBorder':
          return '0'
        default:
          return '0; border-bottom: 1px solid rgba(0, 0, 0, 0.1)'
      }
    })(),
    'padding': '0',
    'borderRadius': (() => {
      switch (props.variant) {
        case 'rounded':
          return '4px'
        case 'square':
        default:
          return '0px'
      }
    })(),
    '&:hover': {
      borderColor: 'rgba(0, 0, 0, 0.3)',
    },
    '&:focus': {
      'borderColor': '#aaa',
      'boxShadow': '0 0 1px 3px rgba(59, 153, 252, 0.7)',
      '-moz-box-shadow': '0 0 0 3px -moz-mac-focusring',
      'outline': 'none',
    },
  }),
)

const IconWrapper = styled.span`
  position: absolute;
  right: 0;
  top: 50%;
  margin-top: -${(props) => props.size / 2}px;
`

const Dropdown = muiStyled('ul')(({ theme, openFrom, ...props }) => ({
  position: 'absolute',
  zIndex: 1,
  top: 0,
  right: openFrom === 'top right' ? 'right: 0;' : undefined,
  margin: 0,
  padding: 0,
  boxSizing: 'border-box',
  minWidth: '100%',
  /* TODO: do this dynamically */
  maxHeight: '300px',
  overflowY: 'auto',
  listStyle: 'none',
  background: theme.palette.grey[300],
  boxShadow:
    '0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 5px 5px -3px rgba(0, 0, 0, 0.2)',
  border:
    props.variant === 'rounded'
      ? '1px solid rgba(0, 0, 0, 0.1)'
      : '1px solid transparent',

  borderRadius: props.variant === 'rounded' ? '4px' : '0',
}))

const DropdownListItem = styled.li`
  display: block;
  margin: 0;
  padding: 0;
  ${(props) => {
    switch (props.variant) {
      case 'square':
        return 'border-bottom: 1px solid rgba(0, 0, 0, 0.1)'
      default:
        return ''
    }
  }};
`

const DropdownListButton = muiStyled('button')(({ theme }) => ({
  'display': 'block',
  'flex': 1,
  'width': '100%',
  'cursor': 'pointer',
  'border': 0,
  'borderRadius': 0,
  'appearance': 'none',
  'background': 'transparent',
  'padding': 0,
  'margin': 0,
  'textAlign': 'left',
  'boxShadow': 'none',
  'outline': 'none',
  'transition': 'background 0.2s ease-in-out',
  '&:hover, &:focus': {
    background: theme.palette.grey[300],
  },
}))

const dropdownDefaultStyle = {
  transition: 'transform 0.2s ease-in-out',
  transform: 'scale(0)',
  transformOrigin: 'top left',
}

const dropdownTransitionStyles = {
  entering: { transform: 'scale(0)' },
  entered: { transform: 'scale(1)' },
}

const arrowDefaultStyle = {
  transition: 'transform 0.2s ease-in-out',
  transform: 'rotateX(0deg)',
}

const arrowTransitionStyles = {
  entering: { transform: 'rotateX(0deg)' },
  entered: { transform: 'rotateX(180deg)' },
}

const DropdownSelectList = ({
  className,
  dropdownWidth,
  selectedItem,
  iconSize,
  items,
  onSelect,
  variant,
}) => {
  const [dropdownOpen, toggleDropdown] = useState(false)
  const muiTheme = useTheme()
  const renderDropdown = (transitionProps) => {
    if (items.length > 0) {
      return (
        <Dropdown
          role="listbox"
          style={{
            ...dropdownDefaultStyle,
            ...dropdownTransitionStyles[transitionProps],
          }}
          tabIndex={dropdownOpen ? 0 : -1}
          variant={variant}
        >
          {items.map(renderItem)}
        </Dropdown>
      )
    } else {
      return null
    }
  }

  const onItemClick = (item) => {
    onSelect(item.value)
    toggleDropdown(false)
  }

  const clickOutsideHandler = () => {
    toggleDropdown(false)
  }

  /* eslint-disable react/no-multi-comp */
  // Should convert these buttons to a radio button group for
  // keyboard accessibility https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role#Description
  const renderItem = (item) => {
    return (
      <DropdownListItem
        aria-selected={selectedItem.value === item.value}
        key={item.value}
        role="option"
        variant={variant}
      >
        <DropdownListButton
          onClick={() => onItemClick(item)}
          tabIndex={dropdownOpen ? 0 : -1}
        >
          {item.element}
        </DropdownListButton>
      </DropdownListItem>
    )
  }
  /* eslint-enable react/no-multi-comp */

  return (
    <ClickAwayListener onClickAway={clickOutsideHandler}>
      <Wrapper className={className}>
        <DropdownTrigger
          aria-expanded={dropdownOpen}
          aria-haspopup="listbox"
          dropdownWidth={dropdownWidth}
          onClick={() => {
            toggleDropdown(!dropdownOpen)
          }}
          variant={variant}
        >
          {selectedItem.element}
          <Transition in={dropdownOpen} timeout={0}>
            {(transition) => (
              <IconWrapper
                size={iconSize}
                style={{
                  ...arrowDefaultStyle,
                  ...arrowTransitionStyles[transition],
                }}
              >
                <Icon
                  color={muiTheme.palette.grey[800]}
                  icon={'caret_down'}
                  size={iconSize}
                />
              </IconWrapper>
            )}
          </Transition>
        </DropdownTrigger>
        <Transition in={dropdownOpen} timeout={0}>
          {renderDropdown}
        </Transition>
      </Wrapper>
    </ClickAwayListener>
  )
}

DropdownSelectList.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  dropdownWidth: PropTypes.oneOf(['full', 'dynamic']),
  iconSize: PropTypes.number,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      element: PropTypes.node,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ).isRequired,
  onSelect: PropTypes.func,
  selectedItem: PropTypes.shape({
    element: PropTypes.node,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  theme: PropTypes.oneOf(['rounded', 'square']),
}

DropdownSelectList.defaultProps = {
  dropdownWidth: 'dynamic',
  items: [],
  iconSize: 25,
  onSelect: () => {},
  variant: 'rounded',
}

export default DropdownSelectList
